import { useEffect, useLayoutEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  useUserContextController,
  setIsUserLoading,
  setUser,
  setGoogleCredentials,
  setCompany,
  Company,
  setConnectedAccounts
} from '../context/UserContext';
import { getCredentials, getUser } from '../api/userProfile';
import { GOOGLE_ACCOUNT_TYPE } from '../types/userCredentials';
import { getUserCompany } from '../api/company';
import { fetchConnectedAccounts } from '../api/connector';
import { useSnackbar } from '../context/SnackbarContext';
import { handleCompleteLogout } from '../utils/authHelpers';

export const useUserInitialization = (userSessionToken: string | undefined) => {
  const [_, userDispatch] = useUserContextController();
  const { showSnackbar } = useSnackbar();

  // Runs synchronously after DOM updates, before browser paint — so `isUserLoading`
  // is true before any child route can render and bounce to /login.
  useLayoutEffect(() => {
    if (!userSessionToken) {
      Sentry.setUser(null);
      setIsUserLoading(userDispatch, false);
      return;
    }
    setIsUserLoading(userDispatch, true);
  }, [userSessionToken, userDispatch]);

  useEffect(() => {
    if (!userSessionToken) {
      return;
    }

    const initializeUser = async () => {
      try {
        const [user, company] = await Promise.all([
          getUser().then(async (user) => {
            if (!user) {
              await handleCompleteLogout();
              showSnackbar(
                'User logged out, redirecting to login page',
                'info'
              );
              window.location.reload();
              return null;
            }

            setUser(userDispatch, user);
            Sentry.setUser({
              id: user.user_id,
              email: user.emails?.[0],
              name: user.fullName
            });

            const credentialsResponse = await getCredentials(
              GOOGLE_ACCOUNT_TYPE.WEB_APP,
              user.user_id
            );

            if (credentialsResponse?.credentials?.google) {
              setGoogleCredentials(userDispatch, {
                access_token:
                  credentialsResponse.credentials.google.access_token,
                refresh_token:
                  credentialsResponse.credentials.google.refresh_token
              });
            }

            return user;
          }),
          getUserCompany().then((company: Company) => {
            setCompany(userDispatch, company);
            return company;
          })
        ]);

        // Fetch connected accounts after company is loaded
        const companyData = company as Company | undefined;
        if (companyData?.org_id) {
          try {
            const connectedAccounts = await fetchConnectedAccounts();
            setConnectedAccounts(userDispatch, connectedAccounts);
          } catch (error) {
            console.error('Failed to fetch connected accounts:', error);
            setConnectedAccounts(userDispatch, []);
          }
        }
      } finally {
        setIsUserLoading(userDispatch, false);
      }
    };

    initializeUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSessionToken]);
};
